// PLAN-CAMPO fase 2 — Página pública do planejamento de uma campanha de campo.
// URL: /publico/planejamento/{hash}. A equipe de campo (sem login) vê o
// briefing, as localidades/pontos, os equipamentos e baixa os KMZ.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag, Typography, Button, Spin, Empty } from 'antd';
import { DownloadOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';

const { Title, Text, Paragraph } = Typography;

interface Planejamento {
  titulo: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  data_inicio: string;
  data_fim: string;
  status: string;
  briefing: string | null;
  observacoes: string | null;
  profissionais: string[];
  equipamentos: string[];
  kits?: Array<{ kit_id: string; nome: string; codigo: string | null }>;
  localidades: Array<{ nome?: string; link?: string; obs?: string }>;
  arquivos: Array<{ nome: string; tipo: string; url: string | null }>;
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: 8,
      padding: 16, marginBottom: 14 }}>
      <Text strong style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{titulo}</Text>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

export default function PaginaPublicaPlanejamento({ hash }: { hash: string }) {
  const [data, setData] = useState<Planejamento | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setErro('');
      try {
        const r = await axios.get(`${API_URL}/publico/proposta/${hash}`);
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (!d || (!d.data_inicio && !d.titulo)) { setErro('Link inválido ou revogado.'); return; }
        setData(d);
      } catch (e: any) {
        setErro(e.response?.data?.error || e.message || 'Falha ao carregar');
      } finally { setLoading(false); }
    })();
  }, [hash]);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f4f6fb 0%, #E7ECFD 100%)',
      padding: '24px 16px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : erro || !data ? (
          <div style={{ background: '#fff', borderRadius: 8, padding: 50, textAlign: 'center' }}>
            <div style={{ fontSize: 50, marginBottom: 14 }}>🔒</div>
            <Title level={3} style={{ color: '#020D7F' }}>Link inválido</Title>
            <Text type="secondary">{erro || 'Acesso revogado.'}</Text>
          </div>
        ) : (
          <>
            {/* Cabeçalho de marca */}
            <div style={{ background: 'linear-gradient(135deg, #020D7F 0%, #0930D0 100%)', color: '#fff',
              padding: '22px 26px', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 11, opacity: 0.8, letterSpacing: 0.5 }}>PLANEJAMENTO DE CAMPO</div>
              <h1 style={{ margin: '4px 0 0', fontSize: 20 }}>{data.titulo || data.contrato_titulo || 'Campanha de campo'}</h1>
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {data.contrato_numero && <span><strong>Contrato</strong> {data.contrato_numero}</span>}
                <span><strong>📆</strong> {fmtDataBr(data.data_inicio)} a {fmtDataBr(data.data_fim)}</span>
              </div>
            </div>

            <Secao titulo="Equipe">
              {(data.profissionais || []).length
                ? data.profissionais.map((p, i) => <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{p}</Tag>)
                : <Text type="secondary">—</Text>}
            </Secao>

            {(data.kits || []).length > 0 && (
              <Secao titulo="Kits de equipamento">
                {data.kits!.map((k, i) => <Tag key={i} color="geekblue" style={{ marginBottom: 4 }}>📦 {k.nome}{k.codigo ? ` (${k.codigo})` : ''}</Tag>)}
              </Secao>
            )}

            {(data.equipamentos || []).length > 0 && (
              <Secao titulo="Equipamentos avulsos">
                {data.equipamentos.map((e, i) => <Tag key={i} style={{ marginBottom: 4 }}>🧰 {e}</Tag>)}
              </Secao>
            )}

            {(data.localidades || []).length > 0 && (
              <Secao titulo="Localidades / pontos">
                {data.localidades.map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    borderBottom: i < data.localidades.length - 1 ? '0.5px solid #f0f0f0' : 'none' }}>
                    <EnvironmentOutlined style={{ color: '#0930D0', flexShrink: 0 }} />
                    {/* Mesmo cuidado do bloco de arquivos abaixo: a observação é
                        texto livre e pode trazer um link ou coordenada colada,
                        que empurraria o "Abrir mapa" para fora. */}
                    <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                      <Text>{l.nome || 'Ponto'}</Text>
                      {l.obs && <div><Text type="secondary" style={{ fontSize: 11 }}>{l.obs}</Text></div>}
                    </div>
                    {l.link && (
                      <a href={l.link} target="_blank" rel="noopener noreferrer"
                        style={{ flexShrink: 0 }}>
                        <Button size="small" type="primary" style={{ background: '#0930D0', borderColor: '#0930D0' }}>Abrir mapa</Button>
                      </a>
                    )}
                  </div>
                ))}
              </Secao>
            )}

            {(data.arquivos || []).length > 0 && (
              <Secao titulo="Arquivos (KMZ / mapas)">
                {data.arquivos.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    borderBottom: i < data.arquivos.length - 1 ? '0.5px solid #f0f0f0' : 'none' }}>
                    {/* Nome de arquivo é token contínuo, sem espaço para quebrar
                        ("Planejamento_Batimetria_Cachoeirinha_2026-08.kmz"), e
                        item de flex não encolhe abaixo do próprio conteúdo — o
                        botão Baixar era empurrado para fora. Esta é uma página
                        PÚBLICA e baixar é a única coisa que o cliente faz aqui. */}
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{a.tipo === 'kml' ? '🗺️' : a.tipo === 'pdf' ? '📄' : '📎'}</span>
                    <Text style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{a.nome}</Text>
                    {a.url
                      ? <a href={a.url} style={{ flexShrink: 0 }}><Button size="small" icon={<DownloadOutlined />}>Baixar</Button></a>
                      : <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>indisponível</Text>}
                  </div>
                ))}
              </Secao>
            )}

            {data.briefing && (
              <Secao titulo="Briefing técnico">
                <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{data.briefing}</Paragraph>
              </Secao>
            )}
            {data.observacoes && (
              <Secao titulo="Observações">
                <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{data.observacoes}</Paragraph>
              </Secao>
            )}

            {!data.briefing && !(data.localidades || []).length && !(data.arquivos || []).length && (
              <Empty description="Detalhes ainda não preenchidos." />
            )}

            <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginTop: 24,
              paddingTop: 14, borderTop: '0.5px solid #d8dcec' }}>
              Hydrodata Engenharia · Planejamento de campo · Acesso por link
            </div>
          </>
        )}
      </div>
    </div>
  );
}
